import { DictionaryProvider, DictionaryExpression, DynamicDictionaryExpression } from "./Dictionary";
import { Scalar, ScalarProvider, scalarValue } from "./Scalar";
import { MutableVariable } from "./Variable";

export class DictionaryMap<KeyType, ValueInType, ValueOutType> extends DictionaryExpression<
	KeyType,
	ValueInType,
	Scalar<ValueOutType>
> {
	private _entries = new Map<KeyType, Scalar<ValueOutType>>();

	constructor(
		source: DictionaryProvider<KeyType, ValueInType>,
		private readonly map_fn: ($: [KeyType, ValueInType]) => Scalar<ValueOutType>,
		readonly __origin?: string
	) {
		super(source);
	}

	add(key: KeyType, value: ValueInType) {
		const outvalue = this.map_fn([key, value]);
		this._entries.set(key, outvalue);
		this._subscriber!.add(key, outvalue);
		this._size?.ensure(this._entries.size);
	}

	remove(key: KeyType) {
		this._entries.delete(key);
		this._subscriber!.remove(key);
		this._size?.ensure(this._entries.size);
	}

	get entries(): ReadonlyMap<KeyType, ValueOutType> {
		if (this._subscriber === undefined) {
			const entries = new Map<KeyType, ValueOutType>();
			for (const $ of this.source.entries) {
				entries.set($[0], scalarValue(this.map_fn($)));
			}
			return entries;
		} else {
			return new Map([...this._entries].map(([k, v]) => [k, scalarValue(v)] as const));
		}
	}

	private _size: MutableVariable<number> | undefined;
	get size(): ScalarProvider<number> {
		return (this._size ??= new MutableVariable(this._entries.size));
	}
}

export class DynamicDictionaryMap<KeyType, ValueInType, ValueOutType> extends DynamicDictionaryExpression<
	KeyType,
	ValueInType,
	DictionaryMap<KeyType, ValueInType, ValueOutType>
> {
	constructor(
		source: ScalarProvider<DictionaryProvider<KeyType, ValueInType>>,
		private readonly map_fn: ($: [KeyType, ValueInType]) => ValueOutType,
		readonly __origin?: string
	) {
		super(source);
	}

	initialize() {
		return new DictionaryMap<KeyType, ValueInType, ValueOutType>(this.source.value, this.map_fn);
	}
}
